<template>
  <div class="subscription-page">
    <div class="subscription-shell">
      <div class="admin-nav">
        <ul class="nav">
          <li><a href="#">Home</a></li>
          <li><a href="#">Admin</a></li>
          <li><a href="#" aria-current="page">Subscriptions</a></li>
        </ul>
      </div>

      <section class="subscription-hero">
        <div class="hero-copy">
          <p class="hero-kicker">Subscription Workspace</p>
          <h1>Track renewal windows, and access status from one admin view.</h1>
          <p class="hero-description">
            This workspace now reads from the live subscriptions endpoint
          </p>
        </div>

        <div class="hero-stats">
          <article class="stat-card">
            <span class="stat-label">Total subscriptions</span>
            <strong class="stat-value">{{ normalizedSubscriptions.length }}</strong>
          </article>
          <article class="stat-card">
            <span class="stat-label">Active</span>
            <strong class="stat-value">{{ activeSubscriptionsCount }}</strong>
          </article>
          <article class="stat-card">
            <span class="stat-label">Renewing soon</span>
            <strong class="stat-value">{{ renewingSoonCount }}</strong>
          </article>
        </div>
      </section>

      <section class="workspace-card">
        <div class="section-header">
          <div>
            <p class="section-kicker">Live register</p>
            <h2>Subscriptions</h2>
          </div>
        </div>

        <div class="toolbar">
          <label class="search-shell" for="subscription-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              id="subscription-search"
              v-model.trim="searchQuery"
              type="search"
              placeholder="Search by organization, plan, or status"
            />
          </label>

          <div class="toolbar-meta">
            <label class="page-size-shell" for="subscription-page-size">
              <span>Rows</span>
              <select id="subscription-page-size" v-model.number="perPage">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="20">20</option>
              </select>
            </label>

            <button
              type="button"
              class="refresh-button"
              :disabled="isBusy"
              @click="fetchSubscriptions"
            >
              <i class="fas fa-sync-alt" aria-hidden="true"></i>
              <span>{{ isBusy ? 'Refreshing...' : 'Refresh' }}</span>
            </button>
          </div>
        </div>

        <div v-if="isBusy" class="feedback-state">
          <b-spinner />
          <p>Loading subscriptions...</p>
        </div>

        <div v-else-if="hasError" class="feedback-state feedback-state--error">
          <h3>Unable to load subscriptions</h3>
          <p>{{ errorMessage }}</p>
          <button type="button" class="retry-button" @click="fetchSubscriptions">Try again</button>
        </div>

        <div
          v-else-if="filteredSubscriptions.length === 0"
          class="feedback-state feedback-state--empty"
        >
          <h3>No subscriptions found</h3>
          <p>
            {{
              normalizedSubscriptions.length
                ? 'Try a different search term to widen the results.'
                : 'The endpoint returned no subscription records yet.'
            }}
          </p>
        </div>

        <template v-else>
          <section class="table-shell">
            <div class="table-scroll-hint">Swipe left or right to view more columns</div>

            <div class="table-scroll-shell">
              <b-table
                id="subscription-register"
                thead-class="custom-thead"
                tbody-tr-class="table-row"
                class="premium-table"
                :hover="false"
                :bordered="false"
                :items="filteredSubscriptions"
                :fields="tableFields"
                :current-page="currentPage"
                :per-page="perPage"
                primary-key="id"
                show-empty
              >
                <template #head()="data">
                  <span class="table-head-label">{{ data.label }}</span>
                </template>

                <template #cell(serial)="row">
                  <span class="index-pill">
                    {{ (currentPage - 1) * perPage + row.index + 1 }}
                  </span>
                </template>

                <template #cell(organizationName)="row">
                  <div class="entity-cell">
                    <strong>{{ row.item.organizationName }}</strong>
                  </div>
                </template>

                <template #cell(status)="row">
                  <span class="status-pill" :class="statusClass(row.item.status)">
                    {{ row.item.status }}
                  </span>
                </template>

                <template #cell(yearOfReg)="row">
                  <span>{{ row.item.yearOfReg || '-' }}</span>
                </template>

                <template #cell(expiresOn)="row">
                  <span>{{ formatExpiresYear(row.item.expiresOn) }}</span>
                </template>

                <template #cell(action)="row">
                  <div class="action-wrap">
                    <button
                      type="button"
                      class="table-action-btn"
                      aria-label="Open subscription actions"
                      @click="toggleActionMenu(row.item.id)"
                    >
                      <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                    </button>

                    <div
                      v-if="activeActionMenuId === row.item.id"
                      class="action-popup"
                      :class="{ 'action-popup--top': shouldOpenMenuUp(row.index) }"
                      @mouseleave="closeActionMenu"
                    >
                      <button
                        type="button"
                        class="action-popup__item"
                        @click="handleRenewSubscription(row.item)"
                      >
                        Renew
                      </button>
                      <button
                        type="button"
                        class="action-popup__item action-popup__item--danger"
                        @click="handleDeleteSubscription(row.item)"
                      >
                        Cancel Subscription
                      </button>
                    </div>
                  </div>
                </template>

                <template #head(action)>
                  <p></p>
                </template>
              </b-table>
            </div>
          </section>

          <div v-if="filteredSubscriptions.length" class="table-footer">
            <div class="pagination-summary">
              <span class="pagination-summary__eyebrow">Subscription register</span>
              <p class="pagination-range">
                {{ startRow }} - {{ endRow }} of {{ filteredSubscriptions.length }}
              </p>
            </div>

            <div class="pagination-controls">
              <div class="page-size-switcher" role="group" aria-label="Rows per page">
                <button
                  v-for="size in [5, 10, 20]"
                  :key="size"
                  type="button"
                  class="page-size-chip"
                  :class="{ 'page-size-chip--active': perPage === size }"
                  @click="perPage = size"
                >
                  {{ size }}
                </button>
              </div>

              <b-pagination
                v-model="currentPage"
                :total-rows="filteredSubscriptions.length"
                :per-page="perPage"
                pills
                size="sm"
                class="premium-pagination"
                aria-controls="subscription-register"
              />
            </div>
          </div>
        </template>
      </section>

      <RenewSubscription
        :visible="showRenewModal && !!pendingRenewSubscription"
        :subscription="pendingRenewSubscription"
        :loading="isRenewingSubscription"
        @close="closeRenewModal"
        @confirm="confirmRenewSubscription"
      />

      <DeleteSubscription
        :visible="showDeleteModal && !!pendingDeleteSubscription"
        :subscription="pendingDeleteSubscription"
        :loading="isCancellingSubscription"
        @close="closeDeleteModal"
        @confirm="confirmDeleteSubscription"
      />
    </div>
  </div>
</template>

<script>
import getSubscriptions, {
  cancelOrganizationSubscription,
  renewOrganizationSubscription,
} from '../config/api-modules/api.subscriptions';
import DeleteSubscription from './modal/DeleteSubscription.vue';
import RenewSubscription from './modal/RenewSubscription.vue';

const EMPTY_VALUE = 'Not available';
const RENEWAL_WINDOW_DAYS = 30;

export default {
  name: 'SubscriptionPage',

  components: {
    DeleteSubscription,
    RenewSubscription,
  },

  data() {
    return {
      subscriptions: [],
      isBusy: false,
      hasError: false,
      errorMessage: 'Something went wrong while contacting the subscriptions endpoint.',
      searchQuery: '',
      activeActionMenuId: null,
      showRenewModal: false,
      pendingRenewSubscription: null,
      isRenewingSubscription: false,
      showDeleteModal: false,
      pendingDeleteSubscription: null,
      isCancellingSubscription: false,
      currentPage: 1,
      perPage: 10,
      tableFields: [
        { key: 'serial', label: 'S/N' },
        { key: 'organizationName', label: 'Organization' },
        { key: 'status', label: 'Status' },
        { key: 'yearOfReg', label: 'Year of Reg.' },
        { key: 'expiresOn', label: 'Expires On' },
        { key: 'action', label: '' },
      ],
    };
  },

  computed: {
    normalizedSubscriptions() {
      return this.subscriptions.map((subscription, index) =>
        this.normalizeSubscription(subscription, index)
      );
    },

    filteredSubscriptions() {
      const query = this.searchQuery.toLowerCase();

      if (!query) {
        return this.normalizedSubscriptions;
      }

      return this.normalizedSubscriptions.filter((subscription) =>
        [subscription.organizationName, subscription.status].join(' ').toLowerCase().includes(query)
      );
    },

    activeSubscriptionsCount() {
      return this.normalizedSubscriptions.filter((subscription) => subscription.isActive).length;
    },

    renewingSoonCount() {
      return this.normalizedSubscriptions.filter((subscription) =>
        this.isRenewingSoon(subscription.renewalDate || subscription.endDate)
      ).length;
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredSubscriptions.length / this.perPage));
    },

    startRow() {
      if (!this.filteredSubscriptions.length) {
        return 0;
      }

      return (this.currentPage - 1) * this.perPage + 1;
    },

    endRow() {
      return Math.min(this.currentPage * this.perPage, this.filteredSubscriptions.length);
    },

    visibleRowsOnPage() {
      return Math.min(
        this.perPage,
        Math.max(0, this.filteredSubscriptions.length - (this.currentPage - 1) * this.perPage)
      );
    },
  },

  watch: {
    searchQuery() {
      this.currentPage = 1;
      this.closeActionMenu();
    },

    perPage() {
      this.currentPage = 1;
      this.closeActionMenu();
    },

    filteredSubscriptions() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = 1;
      }

      this.closeActionMenu();
    },
  },

  methods: {
    async fetchSubscriptions() {
      this.isBusy = true;
      this.hasError = false;

      try {
        const response = await getSubscriptions();
        this.subscriptions = this.extractResults(response?.data);
      } catch (error) {
        this.subscriptions = [];
        this.hasError = true;
        this.errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'Something went wrong while contacting the subscriptions endpoint.';
      } finally {
        this.isBusy = false;
      }
    },

    toggleActionMenu(subscriptionId) {
      this.activeActionMenuId = this.activeActionMenuId === subscriptionId ? null : subscriptionId;
    },

    closeActionMenu() {
      this.activeActionMenuId = null;
    },

    openRenewModal(subscription) {
      this.pendingRenewSubscription = subscription;
      this.showRenewModal = true;
      this.closeActionMenu();
    },

    closeRenewModal() {
      this.showRenewModal = false;
      this.pendingRenewSubscription = null;
    },

    openDeleteModal(subscription) {
      this.pendingDeleteSubscription = subscription;
      this.showDeleteModal = true;
      this.closeActionMenu();
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.pendingDeleteSubscription = null;
    },

    extractResults(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (Array.isArray(payload?.results)) {
        return payload.results;
      }

      if (Array.isArray(payload?.data)) {
        return payload.data;
      }

      if (Array.isArray(payload?.data?.results)) {
        return payload.data.results;
      }

      return [];
    },

    normalizeSubscription(subscription, index) {
      const rawStatus = this.firstDefined([
        subscription?.status,
        subscription?.subscription_status,
        subscription?.state,
        subscription?.active === true ? 'Active' : null,
        subscription?.active === false ? 'Inactive' : null,
        subscription?.is_active === true ? 'Active' : null,
        subscription?.is_active === false ? 'Inactive' : null,
      ]);

      return {
        originalIndex: index,
        id:
          subscription?.id || subscription?.subscription_id || subscription?.uuid || `${index + 1}`,
        organizationId: this.firstDefined([
          subscription?.organization,
          subscription?.organization_id,
          subscription?.organization?.id,
          subscription?.organization?.pk,
          subscription?.org_id,
          subscription?.organization_details?.id,
          null,
        ]),
        organizationName: this.firstDefined([
          subscription?.organization_name,
          subscription?.organization?.name,
          subscription?.organization?.organization_name,
          subscription?.organization?.title,
          subscription?.org_name,
          subscription?.name,
          EMPTY_VALUE,
        ]),
        planName: this.firstDefined([
          subscription?.plan_name,
          subscription?.plan_title,
          subscription?.package_name,
          subscription?.subscription_type,
          subscription?.tier_name,
          subscription?.plan?.name,
          subscription?.plan?.title,
          subscription?.subscription_plan?.name,
          subscription?.subscription_plan?.title,
          subscription?.package?.name,
          typeof subscription?.plan === 'string' ? subscription.plan : null,
          EMPTY_VALUE,
        ]),
        status: this.formatLabel(rawStatus || 'Unknown'),
        isActive: Boolean(
          subscription?.active === true ||
            subscription?.is_active === true ||
            this.isActiveStatus(rawStatus)
        ),
        yearOfReg: this.firstDefined([
          subscription?.cac_registration_year,
          subscription?.organization?.cac_registration_year,
          subscription?.organization_details?.cac_registration_year,
          subscription?.organization_data?.cac_registration_year,
          subscription?.ngo?.cac_registration_year,
          null,
        ]),
        startDate: this.firstDefined([
          subscription?.renewed_on,
          subscription?.start_date,
          subscription?.startDate,
          subscription?.starts_at,
          subscription?.subscription_start,
          subscription?.subscription_start_date,
          subscription?.active_from,
          subscription?.created_at,
          null,
        ]),
        endDate: this.firstDefined([
          subscription?.expires_on,
          subscription?.end_date,
          subscription?.endDate,
          subscription?.expires_at,
          subscription?.subscription_end,
          subscription?.subscription_end_date,
          subscription?.expiration_date,
          subscription?.active_to,
          null,
        ]),
        expiresOn: subscription?.expires_on || null,
        renewalDate: this.firstDefined([
          subscription?.renewed_on,
          subscription?.year_renewed ? this.buildDateFromYear(subscription?.year_renewed, 0) : null,
          subscription?.renewal_date,
          subscription?.renewalDate,
          subscription?.next_billing_date,
          subscription?.next_payment_date,
          subscription?.renewed_at,
          subscription?.updated_at,
          subscription?.expires_at,
          subscription?.end_date,
          subscription?.year ? this.buildDateFromYear(subscription?.year, 0) : null,
          null,
        ]),
      };
    },

    resolveBaseRenewalDate(subscription) {
      return (
        subscription?.renewalDate ||
        subscription?.endDate ||
        this.firstDefined([
          subscription?.renewal_date,
          subscription?.next_billing_date,
          subscription?.next_payment_date,
          subscription?.expires_at,
          subscription?.end_date,
          null,
        ]) ||
        new Date().toISOString()
      );
    },

    buildRenewedDate(value, year) {
      const baseDate = new Date(value);
      const parsedYear = Number(year);

      if (Number.isNaN(parsedYear)) {
        return value;
      }

      const renewedDate = Number.isNaN(baseDate.getTime()) ? new Date() : new Date(baseDate);
      renewedDate.setFullYear(parsedYear);
      return renewedDate.toISOString();
    },

    buildDateFromYear(year, yearOffset = 0) {
      const parsedYear = Number(year);

      if (Number.isNaN(parsedYear)) {
        return null;
      }

      return new Date(parsedYear + yearOffset, 0, 1).toISOString();
    },

    async confirmRenewSubscription(payload) {
      const subscription = this.pendingRenewSubscription;
      const renewalYear = payload?.year;

      if (!subscription) {
        this.closeRenewModal();
        return;
      }

      if (!subscription.organizationId) {
        if (this.$toast?.show) {
          this.$toast.show({
            type: 'ERROR',
            message: 'Unable to renew subscription: organization id is missing',
          });
        }
        return;
      }

      const targetIndex = Number(subscription.originalIndex);

      if (Number.isNaN(targetIndex) || !this.subscriptions[targetIndex]) {
        this.closeRenewModal();
        return;
      }

      this.isRenewingSubscription = true;

      try {
        await renewOrganizationSubscription(subscription.organizationId, {
          year: renewalYear,
        });

        const currentRecord = this.subscriptions[targetIndex];
        const renewedStartDate =
          this.buildDateFromYear(renewalYear, 0) ||
          this.buildRenewedDate(this.resolveBaseRenewalDate(subscription), renewalYear);
        const renewedEndDate =
          this.buildDateFromYear(renewalYear, 2) ||
          this.buildRenewedDate(this.resolveBaseRenewalDate(subscription), Number(renewalYear) + 2);

        this.subscriptions.splice(targetIndex, 1, {
          ...currentRecord,
          year: renewalYear,
          renewal_date: renewedStartDate,
          next_billing_date: renewedStartDate,
          next_payment_date: renewedStartDate,
          start_date: renewedStartDate,
          subscription_start_date: renewedStartDate,
          active_from: renewedStartDate,
          expires_at: renewedEndDate,
          end_date: renewedEndDate,
          subscription_end_date: renewedEndDate,
          active_to: renewedEndDate,
          status: currentRecord?.status || 'active',
          subscription_status: currentRecord?.subscription_status || 'active',
          is_active: true,
        });

        this.closeRenewModal();

        if (this.$toast?.show) {
          this.$toast.show({
            type: 'SUCCESS',
            message: renewalYear
              ? `Subscription renewed for ${renewalYear}`
              : 'Subscription renewed successfully',
          });
        }
      } catch (error) {
        if (this.$toast?.show) {
          this.$toast.show({
            type: 'ERROR',
            message:
              error?.response?.data?.message ||
              error?.response?.data?.detail ||
              'Unable to renew subscription',
          });
        }
      } finally {
        this.isRenewingSubscription = false;
      }
    },

    handleRenewSubscription(subscription) {
      this.openRenewModal(subscription);
    },

    handleDeleteSubscription(subscription) {
      this.openDeleteModal(subscription);
    },

    async confirmDeleteSubscription() {
      const subscription = this.pendingDeleteSubscription;

      if (!subscription) {
        this.closeDeleteModal();
        return;
      }

      if (!subscription.organizationId) {
        if (this.$toast?.show) {
          this.$toast.show({
            type: 'ERROR',
            message: 'Unable to cancel subscription: organization id is missing',
          });
        }
        return;
      }

      const targetIndex = Number(subscription.originalIndex);

      if (Number.isNaN(targetIndex)) {
        this.closeDeleteModal();
        return;
      }

      this.isCancellingSubscription = true;

      try {
        await cancelOrganizationSubscription(subscription.organizationId);

        this.subscriptions = this.subscriptions.filter((_, index) => index !== targetIndex);
        this.closeDeleteModal();

        if (this.$toast?.show) {
          this.$toast.show({
            type: 'SUCCESS',
            message: 'Subscription cancelled successfully',
          });
        }
      } catch (error) {
        if (this.$toast?.show) {
          this.$toast.show({
            type: 'ERROR',
            message:
              error?.response?.data?.message ||
              error?.response?.data?.detail ||
              'Unable to cancel subscription',
          });
        }
      } finally {
        this.isCancellingSubscription = false;
      }
    },

    shouldOpenMenuUp(rowIndex) {
      return rowIndex >= Math.max(0, this.visibleRowsOnPage - 2);
    },

    firstDefined(values) {
      return values.find((value) => value !== undefined && value !== null && value !== '');
    },

    formatLabel(value) {
      return String(value)
        .replace(/[_-]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },

    formatAmount(amount, currency) {
      if (amount === undefined || amount === null || amount === '') {
        return EMPTY_VALUE;
      }

      const numericAmount = Number(amount);

      if (!Number.isNaN(numericAmount)) {
        return `${currency} ${numericAmount.toLocaleString()}`;
      }

      return `${currency} ${amount}`;
    },

    formatDate(value) {
      if (!value) {
        return '-';
      }

      const parsedDate = new Date(value);

      if (Number.isNaN(parsedDate.getTime())) {
        return value;
      }

      return parsedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatExpiresYear(value) {
      if (!value) {
        return '-';
      }

      const isoYearMatch = String(value).match(/^(\d{4})-/);
      if (isoYearMatch) {
        return isoYearMatch[1];
      }

      const parsedDate = new Date(value);

      if (!Number.isNaN(parsedDate.getTime())) {
        return String(parsedDate.getUTCFullYear());
      }

      const yearMatch = String(value).match(/\b\d{4}\b/);
      return yearMatch ? yearMatch[0] : value;
    },

    isActiveStatus(status) {
      return ['active', 'current', 'trialing'].includes(String(status).toLowerCase());
    },

    isRenewingSoon(value) {
      if (!value) {
        return false;
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return false;
      }

      const now = new Date();
      const diffInMilliseconds = date.getTime() - now.getTime();
      const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);

      return diffInDays >= 0 && diffInDays <= RENEWAL_WINDOW_DAYS;
    },

    statusClass(status) {
      const normalizedStatus = String(status).toLowerCase();

      if (['active', 'current', 'trialing', 'renewed'].includes(normalizedStatus)) {
        return 'status-pill--active';
      }

      if (
        ['expired', 'inactive', 'cancelled', 'canceled', 'suspended'].includes(normalizedStatus)
      ) {
        return 'status-pill--inactive';
      }

      return 'status-pill--pending';
    },
  },

  mounted() {
    this.fetchSubscriptions();
  },
};
</script>

<style scoped>
.subscription-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2.5rem;
  background: radial-gradient(circle at top left, rgba(44, 154, 69, 0.14), transparent 24%),
    radial-gradient(circle at top right, rgba(17, 76, 47, 0.08), transparent 20%),
    linear-gradient(180deg, #f4faf5 0%, #eef5ef 46%, #f8fbf8 100%);
}

.subscription-shell {
  max-width: 1320px;
  margin: 0 auto;
}

.admin-nav {
  margin-bottom: 1rem;
}

.admin-nav .nav {
  gap: 0.6rem;
  padding: 0;
}

.admin-nav ul li {
  list-style: none;
}

.admin-nav ul li a {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
  color: #40604a;
  font-size: 0.82rem;
  text-decoration: none;
}

.subscription-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, 1fr);
  gap: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(22, 68, 37, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(12, 40, 21, 0.96), rgba(31, 103, 53, 0.88)),
    linear-gradient(140deg, #16391f 0%, #2ca34a 100%);
  box-shadow: 0 28px 80px rgba(18, 44, 27, 0.16);
  color: #f7fff7;
}

.hero-kicker,
.section-kicker {
  margin-bottom: 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-kicker {
  color: rgba(247, 255, 247, 0.72);
}

.subscription-hero h1 {
  margin-bottom: 0.85rem;
  font-size: clamp(2rem, 3vw, 3.1rem);
  line-height: 1.08;
  color: #ffffff;
}

.hero-description {
  margin: 0;
  max-width: 44rem;
  color: rgba(247, 255, 247, 0.8);
  line-height: 1.75;
}

.hero-stats {
  display: grid;
  gap: 1rem;
}

.stat-card {
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
}

.stat-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8rem;
  color: rgba(247, 255, 247, 0.72);
}

.stat-value {
  font-size: clamp(1.45rem, 3vw, 2.3rem);
  color: #ffffff;
}

.workspace-card {
  margin-top: 1.5rem;
  padding: 1.5rem;
  border: 1px solid rgba(22, 68, 37, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 48px rgba(22, 68, 37, 0.08);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.2rem;
}

.section-header h2 {
  margin: 0;
  color: #17351f;
  font-size: clamp(1.35rem, 2.4vw, 2rem);
}

.section-kicker {
  color: #2b7a42;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}

.search-shell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: min(100%, 24rem);
  padding: 0 1rem;
  border: 1px solid rgba(21, 70, 37, 0.12);
  border-radius: 18px;
  background: #f8fbf8;
}

.search-shell i {
  color: #2b7a42;
}

.search-shell input {
  width: 100%;
  padding: 0.95rem 0;
  border: none;
  outline: none;
  background: transparent;
  color: #17351f;
}

.toolbar-meta {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.page-size-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.78rem 0.95rem;
  border: 1px solid rgba(21, 70, 37, 0.12);
  border-radius: 16px;
  background: #f8fbf8;
  color: #40604a;
}

.page-size-shell select {
  border: none;
  background: transparent;
  color: #17351f;
  outline: none;
}

.refresh-button,
.retry-button {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 1rem;
  border: none;
  border-radius: 16px;
  background: #1f7a38;
  color: #ffffff;
  font-weight: 700;
}

.refresh-button:disabled {
  opacity: 0.7;
}

.feedback-state {
  display: grid;
  place-items: center;
  gap: 0.75rem;
  min-height: 18rem;
  padding: 2rem;
  border: 1px dashed rgba(21, 70, 37, 0.16);
  border-radius: 24px;
  text-align: center;
  color: #40604a;
  background: #fbfdfb;
}

.feedback-state h3,
.feedback-state p {
  margin: 0;
}

.feedback-state--error {
  color: #8a2e2e;
  border-color: rgba(138, 46, 46, 0.18);
  background: #fff8f8;
}

.table-shell {
  overflow: hidden;
  padding: 1.1rem;
  border-radius: 24px;
  border: 1px solid rgba(22, 68, 37, 0.08);
  background: linear-gradient(180deg, rgba(250, 252, 250, 0.96), rgba(239, 245, 240, 0.94));
}

.premium-table {
  margin-bottom: 0;
}

.table-scroll-hint {
  display: none;
  margin-bottom: 0.75rem;
  color: #5f7267;
  font-size: 0.8rem;
  font-weight: 600;
}

.table-scroll-shell {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  border-radius: 18px;
  scrollbar-width: thin;
  scrollbar-color: rgba(34, 111, 57, 0.45) rgba(237, 244, 238, 0.9);
}

.table-scroll-shell::-webkit-scrollbar {
  height: 9px;
}

.table-scroll-shell::-webkit-scrollbar-track {
  background: rgba(237, 244, 238, 0.9);
  border-radius: 999px;
}

.table-scroll-shell::-webkit-scrollbar-thumb {
  background: rgba(34, 111, 57, 0.45);
  border-radius: 999px;
}

.premium-table ::v-deep table {
  background: transparent;
  min-width: 760px;
  table-layout: auto;
}

.premium-table ::v-deep thead th {
  padding: 1rem 0.85rem;
  border-top: 0;
  border-bottom: 1px solid rgba(28, 68, 38, 0.08);
  background: rgba(48, 171, 76, 0.1);
  white-space: nowrap;
}

.premium-table ::v-deep tbody td {
  padding: 1rem 0.85rem;
  vertical-align: middle;
  border-top: 1px solid rgba(28, 68, 38, 0.06);
  background: rgba(255, 255, 255, 0.8);
  color: #234031;
}

.premium-table ::v-deep tbody tr:hover td {
  background: rgba(247, 251, 248, 0.96);
}

.table-head-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4a6853;
  white-space: nowrap;
}

.index-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.3rem;
  min-height: 2.1rem;
  padding: 0 0.65rem;
  border-radius: 999px;
  background: #e8f5eb;
  color: #226c34;
  font-size: 0.78rem;
  font-weight: 700;
}

.entity-cell strong {
  display: inline-block;
  min-width: 180px;
  max-width: 16rem;
  word-break: break-word;
  white-space: normal;
  color: #17351f;
  font-weight: 700;
}

.premium-table ::v-deep th:nth-child(3),
.premium-table ::v-deep td:nth-child(3) {
  min-width: 135px;
}

.premium-table ::v-deep th:nth-child(4),
.premium-table ::v-deep td:nth-child(4) {
  min-width: 120px;
}

.premium-table ::v-deep th:nth-child(5),
.premium-table ::v-deep td:nth-child(5) {
  min-width: 120px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.status-pill--active {
  background: rgba(44, 163, 74, 0.14);
  color: #1f7a38;
}

.status-pill--inactive {
  background: rgba(180, 57, 57, 0.12);
  color: #a43030;
}

.status-pill--pending {
  background: rgba(185, 124, 22, 0.14);
  color: #9b640d;
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.1rem;
  padding: 1rem 1.1rem;
  border-radius: 20px;
  border: 1px solid rgba(29, 70, 44, 0.08);
  background: linear-gradient(180deg, rgba(247, 250, 248, 0.98), rgba(240, 246, 242, 0.92));
}

.pagination-summary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.pagination-summary__eyebrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.08);
  color: #2a8a43;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.pagination-range {
  margin: 0;
  color: #5a7465;
  font-weight: 600;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-left: auto;
  min-width: 0;
}

.page-size-switcher {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(29, 70, 44, 0.08);
}

.page-size-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  height: 2.35rem;
  line-height: 1;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #587062;
  font-weight: 700;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.page-size-chip:hover {
  background: rgba(42, 138, 67, 0.08);
  color: #226c34;
}

.page-size-chip--active {
  background: linear-gradient(135deg, #2f8d49, #215f33);
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(42, 138, 67, 0.16);
}

.subscription-page ::v-deep .premium-pagination {
  margin-bottom: 0;
}

.subscription-page ::v-deep .premium-pagination .page-item .page-link {
  border: 0;
  border-radius: 999px !important;
  min-width: 2.35rem;
  height: 2.35rem;
  margin: 0 0.18rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #4f6d5a;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: none;
}

.subscription-page ::v-deep .premium-pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #2f8d49, #215f33);
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(42, 138, 67, 0.16);
}

.subscription-page ::v-deep .premium-pagination .page-item.disabled .page-link {
  color: #a3b3a8;
  background: rgba(255, 255, 255, 0.56);
}

.action-wrap {
  position: relative;
  display: flex;
  justify-content: center;
}

.table-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 0;
  border-radius: 12px;
  background: #edf7ef;
  color: #256536;
}

.action-popup {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  display: grid;
  gap: 0.35rem;
  min-width: 9.5rem;
  padding: 0.65rem;
  border: 1px solid rgba(28, 68, 38, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 16px 36px rgba(16, 34, 21, 0.12);
  z-index: 20;
}

.action-popup--top {
  top: auto;
  bottom: calc(100% + 0.4rem);
}

.action-popup__item {
  width: 100%;
  padding: 0.7rem 0.8rem;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #234031;
  font-weight: 600;
  text-align: left;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.action-popup__item:hover {
  background: #edf7ef;
  color: #226c34;
}

.action-popup__item--danger:hover {
  background: #fdecec;
  color: #bb4e4e;
}

@media (max-width: 991px) {
  .subscription-page {
    padding-top: 1.75rem;
  }

  .subscription-hero {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .subscription-page {
    padding: 1.5rem 0.5rem 2rem;
  }

  .subscription-hero,
  .workspace-card {
    padding: 1rem;
  }

  .toolbar,
  .table-footer {
    align-items: stretch;
  }

  .toolbar-meta {
    justify-content: space-between;
  }

  .search-shell,
  .page-size-shell,
  .refresh-button,
  .retry-button,
  .pagination-controls,
  .page-size-switcher {
    width: 100%;
  }

  .table-scroll-hint {
    display: block;
  }

  .premium-table ::v-deep table {
    min-width: 700px;
  }

  .premium-table ::v-deep thead th,
  .premium-table ::v-deep tbody td {
    padding: 0.85rem 0.7rem;
  }

  .pagination-controls {
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;
    align-items: center;
    padding-bottom: 0.2rem;
    -webkit-overflow-scrolling: touch;
  }

  .subscription-page ::v-deep .premium-pagination {
    flex-shrink: 0;
  }

  .subscription-page ::v-deep .premium-pagination .pagination {
    flex-wrap: nowrap;
  }
}

@media (max-width: 600px) {
  .subscription-page {
    padding-top: 0.85rem;
  }

  .premium-table ::v-deep table {
    min-width: 660px;
  }
}
</style>
